import { Link } from 'react-router-dom';
import type { GameInfo } from '@/types/games';

interface GameCardProps {
  game: GameInfo;
  bestScore?: number;
  gamesPlayed?: number;
}

export default function GameCard({ game, bestScore, gamesPlayed }: GameCardProps) {
  const difficultyColors: Record<string, string> = {
    'Facile': 'bg-green-100 text-green-800',
    'Moyen': 'bg-amber-100 text-amber-800',
    'Difficile': 'bg-red-100 text-red-800',
  };

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-accent-500/30 transition-all duration-300 hover:scale-[1.02] group cursor-pointer flex flex-col">
      <div className="relative h-40 overflow-hidden">
        <img
          src={game.imageUrl}
          alt={game.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${difficultyColors[game.difficulty] || 'bg-gray-100 text-gray-700'}`}>
            {game.difficulty}
          </span>
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-50/80 text-foreground-950 backdrop-blur-sm">
            {game.durationMinutes} min
          </span>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {game.category.slice(0, 2).map((cat) => (
            <span key={cat} className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md">
              {cat}
            </span>
          ))}
          {game.category.length > 2 && (
            <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md">
              +{game.category.length - 2}
            </span>
          )}
        </div>

        <h3 className="font-semibold text-foreground-950 mb-1.5 text-base group-hover:text-accent-500 transition-colors">{game.name}</h3>
        <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1 line-clamp-2">{game.description}</p>

        {(bestScore !== undefined || gamesPlayed !== undefined) && (
          <div className="flex gap-4 mb-3 text-xs text-foreground-700">
            {bestScore !== undefined && bestScore > 0 && (
              <span className="flex items-center gap-1"><i className="ri-star-fill text-amber-500"></i> {bestScore} pts</span>
            )}
            {gamesPlayed !== undefined && gamesPlayed > 0 && (
              <span className="flex items-center gap-1"><i className="ri-gamepad-line"></i> {gamesPlayed} parties</span>
            )}
          </div>
        )}

        <Link
          to={game.path}
          className="mt-auto w-full text-center py-2.5 bg-primary-500 text-background-50 rounded-xl font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-play-fill"></i>
          Jouer
        </Link>
      </div>
    </div>
  );
}